'use client';

import Link from 'next/link';
import { FormEvent, useEffect, useState } from 'react';
import {
  createVendorLinkRequest,
  listLinkedClaims,
  listStorefrontPages,
  listUrlMatchSuggestions,
  listVendorLinkRequests,
  type ClaimPurchaseFields,
  type StorefrontPage,
  type VendorLinkRequest,
} from './api';

export interface VendorClaimLinksPanelProps {
  vendorUserId: string;
  readOnly?: boolean;
}

export function VendorClaimLinksPanel({ vendorUserId, readOnly = false }: VendorClaimLinksPanelProps) {
  const [pages, setPages] = useState<StorefrontPage[]>([]);
  const [requests, setRequests] = useState<VendorLinkRequest[]>([]);
  const [suggestions, setSuggestions] = useState<ClaimPurchaseFields[]>([]);
  const [linked, setLinked] = useState<ClaimPurchaseFields[]>([]);

  useEffect(() => {
    listStorefrontPages(vendorUserId).then((r) => setPages(r.pages)).catch(() => undefined);
    listVendorLinkRequests(vendorUserId).then((r) => setRequests(r.requests)).catch(() => undefined);
    listUrlMatchSuggestions(vendorUserId).then((r) => setSuggestions(r.claims)).catch(() => undefined);
    listLinkedClaims(vendorUserId).then((r) => setLinked(r.claims)).catch(() => undefined);
  }, [vendorUserId]);

  async function requestLink(claimId: string) {
    await createVendorLinkRequest(claimId);
    const r = await listVendorLinkRequests(vendorUserId);
    setRequests(r.requests);
  }

  async function refreshSuggestions(e: FormEvent) {
    e.preventDefault();
    const sug = await listUrlMatchSuggestions(vendorUserId);
    setSuggestions(sug.claims);
  }

  return (
    <section>
      <h2 className="vendor-console__section-title">Claim links</h2>
      <p className="vendor-console__hint">
        Link your listings to community haul reports. Claim owners approve requests on their claim pages.
      </p>

      <h3>Owner-approved claims</h3>
      {linked.length === 0 ? (
        <p className="text-muted">No approved claim links yet.</p>
      ) : (
        <ul>
          {linked.map((c) => (
            <li key={c.id}>
              <Link href={`/claims/${c.id}`}>Claim {c.id}</Link>
            </li>
          ))}
        </ul>
      )}

      <h3>Possible matches</h3>
      <p className="vendor-console__hint">
        Based on {pages.length} registered storefront URL{pages.length === 1 ? '' : 's'}.
      </p>
      <form onSubmit={(e) => void refreshSuggestions(e)} className="vendor-console__submit">
        <button type="submit" className="btn btn-secondary" disabled={readOnly}>
          Refresh matches
        </button>
      </form>
      {suggestions.length === 0 ? (
        <p className="text-muted">No URL matches found. Add storefront URLs under Store settings.</p>
      ) : (
        <ul>
          {suggestions.map((c) => (
            <li key={c.id}>
              <Link href={`/claims/${c.id}`}>Claim {c.id}</Link>{' '}
              <button type="button" className="btn btn-secondary" disabled={readOnly} onClick={() => void requestLink(c.id)}>
                Request link
              </button>
            </li>
          ))}
        </ul>
      )}

      <h3>Outgoing requests</h3>
      {requests.length === 0 ? (
        <p className="text-muted">No link requests yet.</p>
      ) : (
        <table className="vendor-console__table">
          <thead>
            <tr>
              <th>Claim</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link href={`/claims/${r.claimId}`}>{r.claimId}</Link>
                </td>
                <td>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
